import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const MATERIAL_LABEL = {
  "304": "304",
  "316": "316",
  galvanized: "GALVA",
  black: "ZWART",
};

const MATERIAL_CHIP = {
  "304": "border-zinc-400 bg-zinc-50 text-zinc-700",
  "316": "border-zinc-700 bg-zinc-100 text-zinc-900",
  galvanized: "border-amber-300 bg-amber-50 text-amber-800",
  black: "border-zinc-700 bg-zinc-800 text-white",
};

const KIT_CHIP = {
  set: "border-emerald-300 bg-emerald-50 text-emerald-800",
  "set+stud": "border-emerald-300 bg-emerald-50 text-emerald-800",
  "set+thread": "border-emerald-300 bg-emerald-50 text-emerald-800",
  din3567: "border-emerald-300 bg-emerald-50 text-emerald-800",
  "din3567-set": "border-emerald-300 bg-emerald-50 text-emerald-800",
  "din3567-half": "border-amber-400 bg-amber-50 text-amber-900",
  half: "border-amber-400 bg-amber-50 text-amber-900",
  cap: "border-sky-300 bg-sky-50 text-sky-800",
  other: "border-zinc-300 bg-zinc-50 text-zinc-700",
};

// Visual config per stock status — dot colour + label + DN chip border accent.
const STOCK_STATUS = {
  in_stock: {
    label: "24u leverbaar",
    dot: "bg-emerald-500",
    pill: "border-emerald-300 bg-emerald-50 text-emerald-800",
    chipDot: "bg-emerald-500",
  },
  lead_time: {
    label: "1-3 werkdagen",
    dot: "bg-amber-500",
    pill: "border-amber-300 bg-amber-50 text-amber-800",
    chipDot: "bg-amber-500",
  },
  on_request: {
    label: "Op aanvraag",
    dot: "bg-zinc-400",
    pill: "border-zinc-300 bg-zinc-100 text-zinc-700",
    chipDot: "bg-zinc-400",
  },
};

/**
 * Catalog card that represents a single *article group*. Inside the card
 * the customer sees:
 *   • Image of the primary family
 *   • Display name
 *   • Material chips (304 / 316 / GALVA / ZWART) — informational
 *   • Kit-type chips (Enkele beugel / Set / Stift / …) — each chip is a
 *     direct link into that specific family so customers skip the picker.
 *   • Total variant count + price range
 */
export default function ArticleGroupCard({ group }) {
  const { t } = useTranslation();
  const link = `/products/${group.primary_id}`;
  const mats = group.materials_available || [];
  const imagesByMat = group.images_by_material || {};
  const kits = group.kit_types_available || [];
  const dns = group.dn_sizes || [];
  const stock = STOCK_STATUS[group.stock_status] || STOCK_STATUS.in_stock;
  const [showDn, setShowDn] = useState(false);

  // Materials with their own uploaded SKU photo — only these become
  // hover/tap-interactive badges that crossfade the hero. Materials missing
  // from this list keep the catalog truthful (no fake promise of a photo).
  const matsWithPhoto = mats.filter((m) => imagesByMat[m]);
  const [activeMat, setActiveMat] = useState(null);
  const fmtUrl = (u) => {
    if (!u) return group.image;
    if (u.startsWith("http")) return u;
    return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
  };
  const heroUrl = fmtUrl(group.image);
  const overlayUrl = activeMat ? fmtUrl(imagesByMat[activeMat]) : null;

  return (
    <div
      className="nibro-card group relative flex flex-col"
      data-testid={`article-group-card-${group.article_group_key}`}
      onMouseEnter={() => setShowDn(true)}
      onMouseLeave={() => {
        setShowDn(false);
        setActiveMat(null);
      }}
    >
      <Link to={link} className="block">
        <div className="nibro-card-header relative">
          <h3 className="font-display text-sm font-bold leading-tight line-clamp-2 min-h-[2.5rem] flex items-center justify-center">
            {group.display_name}
          </h3>
        </div>

        <div className="relative aspect-[4/3] overflow-hidden bg-white p-6">
          <img
            src={heroUrl}
            alt={t("product.image_alt_no_material", { name: group.display_name })}
            loading="lazy"
            className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-105"
          />
          {/* Crossfade overlay for active material — sits on top of base
              image and fades in (200ms) when a material badge is hovered or
              tapped. Reverts to null on mouse-leave (handled at root). */}
          <img
            src={overlayUrl || heroUrl}
            alt={activeMat ? t("product.image_alt", { name: group.display_name, material: activeMat.toUpperCase() }) : ""}
            aria-hidden={!activeMat}
            data-testid={`article-group-card-${group.article_group_key}-overlay`}
            className={`absolute inset-0 h-full w-full object-contain p-6 transition-opacity duration-200 ${
              activeMat ? "opacity-100" : "opacity-0"
            }`}
          />
          {group.standard && (
            <span className="absolute left-3 top-3 rounded border border-nibro-sage bg-white/95 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-nibro-sage-dark">
              {group.standard}
            </span>
          )}
          {matsWithPhoto.length > 0 && (
            <div className="absolute right-3 top-3 z-10 flex flex-wrap justify-end gap-1">
              {matsWithPhoto.map((m) => {
                const isActive = activeMat === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onMouseEnter={() => setActiveMat(m)}
                    onFocus={() => setActiveMat(m)}
                    onTouchStart={(e) => {
                      // On mobile, the first tap previews, the second tap (or
                      // tap outside) reverts — prevents the underlying <Link>
                      // from navigating before the user has seen the swap.
                      if (activeMat !== m) {
                        e.preventDefault();
                        e.stopPropagation();
                        setActiveMat(m);
                      }
                    }}
                    onClick={(e) => {
                      // Block link navigation when interacting with the
                      // material chip so taps only swap the image.
                      e.preventDefault();
                      e.stopPropagation();
                      setActiveMat((cur) => (cur === m ? null : m));
                    }}
                    data-testid={`group-material-chip-${m}`}
                    aria-pressed={isActive}
                    title={`Toon ${MATERIAL_LABEL[m] || m.toUpperCase()}-foto`}
                    className={`rounded border px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider transition ${
                      MATERIAL_CHIP[m] || MATERIAL_CHIP["304"]
                    } ${isActive ? "ring-2 ring-nibro-sage ring-offset-1" : "hover:brightness-95"}`}
                  >
                    {MATERIAL_LABEL[m] || m.toUpperCase()}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Link>

      {/* Kit-type chips — each is its own deep link */}
      {kits.length > 0 && (
        <div className="flex flex-wrap gap-1 px-4 pt-3">
          {kits.map((k) => (
            <Link
              key={k.kit_type}
              to={`/products/${k.family_id}`}
              data-testid={`group-kit-chip-${k.kit_type}`}
              className={`rounded border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider transition hover:brightness-95 ${KIT_CHIP[k.kit_type] || KIT_CHIP.other}`}
              title={`${k.label} — ${k.variant_count}× maten`}
            >
              {k.label}
            </Link>
          ))}
        </div>
      )}

      {/* DN preview — desktop: hover only; mobile: always visible (1-line scroll) */}
      {dns.length > 0 && (
        <div
          className={`mx-4 mt-3 rounded border border-zinc-200 bg-zinc-50/70 px-2.5 py-2 transition-all sm:overflow-hidden ${
            showDn
              ? "sm:max-h-40 sm:border-nibro-sage sm:bg-nibro-sage-pale"
              : "sm:max-h-9"
          }`}
          data-testid={`group-dn-preview-${group.article_group_key}`}
        >
          <div className="mb-1 hidden items-center justify-between sm:flex">
            <span className="label-uppercase text-[9px] text-nibro-sage-dark">
              {dns.length} DN-maten
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider ${stock.pill}`}
              data-testid={`group-stock-pill-${group.stock_status}`}
              title={stock.label}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${stock.dot}`} />
              {stock.label}
            </span>
          </div>
          <div className="flex flex-wrap gap-1 overflow-x-auto sm:flex-wrap">
            {dns.map((dn) => (
              <Link
                key={dn}
                to={`/products/${group.primary_id}?dn=${dn}`}
                data-testid={`group-dn-${dn}`}
                onClick={(e) => e.stopPropagation()}
                title={`DN${dn} — ${stock.label}`}
                className="inline-flex shrink-0 items-center gap-1 rounded border border-zinc-300 bg-white px-1.5 py-0.5 font-mono text-[10px] font-bold text-zinc-700 transition hover:border-nibro-sage hover:bg-nibro-sage hover:text-white"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${stock.chipDot}`} aria-hidden />
                DN{dn}
              </Link>
            ))}
          </div>
        </div>
      )}

      <Link
        to={link}
        className="mt-auto border-t border-zinc-100 px-4 py-3"
      >
        <div className="flex items-center justify-between text-xs">
          <span className="font-mono text-zinc-500">
            {group.variant_count}× maten
          </span>
          {group.price_from != null && (
            <span className="font-display text-base font-bold text-zinc-900">
              v.a. €{group.price_from.toFixed(2)}
            </span>
          )}
        </div>
      </Link>
    </div>
  );
}
